<template>
  <AreaPageContainer>
    <ChartClickthroughs
      :click-data="chartClickData"
      :get-table-data="area.clickthroughFunction"
    />
    <div class="tw-flex tw-gap-10 tw-overflow-x-hidden">
      <div class="tw-bg-white tw-p-12 tw-w-full tw-overflow-x-hidden">
        <AreaHeader />
        <div class="tw-p-10">
          <div>
            <h2 class="tw-font-georgia tw-font-bold tw-text-5xl">Asset Location</h2>
            <AdvisorCoreStripes />
            <p class="tw-mb-4">
              Asset Location is the practice of strategically placing investments in different types of accounts (taxable, tax-deferred, and tax-free) to maximize after-tax returns.
              In taxable accounts, dividends, interest, and capital gains are taxed annually. 
              In tax-deferred accounts (e.g., IRAs, 401(k)s), taxes on dividends, interest, and gains are deferred until withdrawal. 
              In tax-free accounts (e.g., Roth IRAs), investments grow and can be withdrawn without paying taxes on gains, dividends, or interest.
            </p>
          </div>
          <div v-if="area.portfolioChartData && area.portfolioChartData.some(chart => chart.series?.length)">
            <div class="tw-text-center">
              <div class="tw-font-bold tw-text-3xl tw-text-black">
                Current Allocation
              </div>
              <div class="tw-text-lg tw-text-gray-500">
                {{ portfolioName }}
              </div>
            </div>

            <div>
              <div :class="{'tw-flex': true, 'tw-flex-col': !area.showChartsAsRow}">
                <div
                  v-for="(chart, i) in area.portfolioChartData"
                  :key="i"
                  class="tw-grow"
                >
                  <HighChartsWrapper
                    :name="area.name"
                    :chart-options="chart"
                    :custom-text="chartText"
                    chart-type="pie"
                    export-image
                    @chartClick="handleChartClick"
                    @seriesPointClick="handlePortfolioSeriesPointClick"
                    @image-changed="(data) => checkupStore.updateSlideImage(data, area.name)"
                    @image-removed="(data) => checkupStore.removeSlideImage(data)"
                  />
                </div>
              </div>
            </div>

          </div>
          
          <div v-if="area.optimizedPortfolioChartData && area.optimizedPortfolioChartData.some(chart => chart.series?.length)">
            <br>
            <br>
            <div class="tw-text-center">
              <div class="tw-font-bold tw-text-3xl tw-text-black">
                Recommended Allocation
              </div>
              <div class="tw-text-lg tw-text-gray-500">
                {{ optimizedPortfolioName }}
              </div>
            </div>
            <div>
              <div :class="{'tw-flex': true, 'tw-flex-col': !area.showChartsAsRow}">
                <div
                  v-for="(chart, i) in area.optimizedPortfolioChartData"
                  :key="i"
                  class="tw-grow"
                >
                  <HighChartsWrapper
                    :name="area.name"
                    :chart-options="chart"
                    :custom-text="chartText"
                    chart-type="pie"
                    export-image
                    @chartClick="handleChartClick"
                    @seriesPointClick="handleoptimizedPortfolioSeriesPointClick"
                    @image-changed="(data) => checkupStore.updateSlideImage(data, area.name)"
                    @image-removed="(data) => checkupStore.removeSlideImage(data)"
                  />
                </div>
              </div>
            </div>

          </div>

          <!-- Combined Summary Table -->
          <table v-if="hasAnyCharts" class="tw-mt-10 tw-w-full tw-border-collapse tw-border tw-border-gray-200">
            <thead>
              <tr class="tw-bg-gray-100">
                <th class="tw-border tw-border-gray-200 tw-py-2 tw-px-4" rowspan="2">
                  Asset Class
                </th>
                <th
                  v-for="status in taxStatuses"
                  :key="status"
                  class="tw-border tw-border-gray-200 tw-py-2 tw-px-4 tw-text-center"
                  :colspan="hasPortfolio && hasOptimizedPortfolio ? 2 : 1"
                >
                  {{ status }}
                </th>
              </tr>
              <tr class="tw-bg-gray-100">
                <template v-for="status in taxStatuses">
                  <th v-if="hasPortfolio" :key="'sub-current-' + status" class="tw-border tw-border-gray-200 tw-py-2 tw-px-4">
                    Current
                  </th>
                  <th v-if="hasOptimizedPortfolio" :key="'sub-proposed-' + status" class="tw-border tw-border-gray-200 tw-py-2 tw-px-4">
                    Proposed
                  </th>
                </template>
              </tr>
            </thead>
            <tbody>
              <tr class="tw-bg-gray-50 tw-font-semibold">
                <td class="tw-border tw-border-gray-200 tw-py-2 tw-px-4">
                  Total Value
                </td>
                <template v-for="status in taxStatuses">
                  <td v-if="hasPortfolio" :key="'value-current-' + status" class="tw-border tw-border-gray-200 tw-py-2 tw-px-4">
                    {{ formatCurrency(portfolioTaxStatusValues[status]) }}
                  </td>
                  <td v-if="hasOptimizedPortfolio" :key="'value-proposed-' + status" class="tw-border tw-border-gray-200 tw-py-2 tw-px-4">
                    {{ formatCurrency(optimizedPortfolioTaxStatusValues[status]) }}
                  </td>
                </template>
              </tr>
              <tr class="tw-bg-gray-50">
                <td class="tw-border tw-border-gray-200 tw-py-2 tw-px-4">
                  Number of Accounts
                </td>
                <template v-for="status in taxStatuses">
                  <td v-if="hasPortfolio" :key="'accounts-current-' + status" class="tw-border tw-border-gray-200 tw-py-2 tw-px-4">
                    {{ portfolioTaxStatusAccountCounts[status] || 0 }}
                  </td>
                  <td v-if="hasOptimizedPortfolio" :key="'accounts-proposed-' + status" class="tw-border tw-border-gray-200 tw-py-2 tw-px-4">
                    {{ optimizedPortfolioTaxStatusAccountCounts[status] || 0 }}
                  </td>
                </template>
              </tr>
              <tr
                v-for="(assetClass, index) in allAssetClasses"
                :key="'asset-' + index"
              >
                <td class="tw-border tw-border-gray-200 tw-py-2 tw-px-4">
                  <span :style="{ backgroundColor: getLegendColor(assetClass), width: '12px', height: '12px', display: 'inline-block', marginRight: '8px' }" />
                  {{ assetClass }}
                </td>
                <template v-for="status in taxStatuses">
                  <td v-if="hasPortfolio" :key="'pct-current-' + status + '-' + assetClass" class="tw-border tw-border-gray-200 tw-py-2 tw-px-4">
                    {{ getPercentageForAssetClass(getSeriesByName(area.portfolioChartData, status), assetClass) }}
                  </td>
                  <td v-if="hasOptimizedPortfolio" :key="'pct-proposed-' + status + '-' + assetClass" class="tw-border tw-border-gray-200 tw-py-2 tw-px-4">
                    {{ getPercentageForAssetClass(getSeriesByName(area.optimizedPortfolioChartData, status), assetClass) }}
                  </td>
                </template>
              </tr>
            </tbody>
          </table>

          <!-- Shared Legend -->
          <div class="tw-mt-8 tw-flex tw-justify-center">
            <div class="tw-bg-white tw-p-4 tw-rounded tw-shadow">
              <div class="tw-flex tw-flex-wrap tw-gap-4">
                <div v-for="type in accountTypes" :key="type" class="tw-flex tw-items-center">
                  <div :style="{ backgroundColor: getLegendColor(type) }" class="tw-w-4 tw-h-4 tw-mr-2"></div>
                  <span>{{ type }}</span>
                </div>
              </div>
            </div>
          </div>
          <!-- Disclaimer and Methodology -->
          <div class="tw-flex tw-gap-10">
              <!-- <CheckupDisclaimer class="tw-basis-0 tw-grow tw-bg-white !tw-text-[#353a46] tw-px-0" /> -->

              <div class="tw-flex tw-flex-col tw-basis-0 tw-grow tw-py-10 tw-bg-white !tw-text-[#353a46]">
                <div class="mt-4">
                  <!-- Portfolio Unclassified Holdings -->
                  <div v-if="area.portfolioHoldingsWithNoTaxStatus?.length" class="tw-mb-4">
                    <div 
                      class="tw-flex tw-items-center tw-cursor-pointer tw-p-3 tw-bg-gray-100 tw-rounded" 
                      @click="togglePortfolioPanel"
                    >
                      <i class="fas fa-exclamation-circle tw-mr-2 tw-text-yellow-500"></i>
                      <span>Holdings with Unclassified Tax Status in {{ portfolioName }}</span>
                      <i 
                        :class="[
                          'fas',
                          isPortfolioPanelOpen ? 'fa-chevron-up' : 'fa-chevron-down',
                          'tw-ml-auto'
                        ]"
                      ></i>
                    </div>
                    <div v-show="isPortfolioPanelOpen" class="tw-border tw-border-t-0 tw-p-4">
                      <table class="tw-w-full tw-border-collapse">
                        <thead>
                          <tr class="tw-bg-gray-50">
                            <th class="tw-p-3 tw-text-left !tw-text-black tw-font-bold">Symbol</th>
                            <th class="tw-p-3 tw-text-left !tw-text-black tw-font-bold">Account</th>
                            <th class="tw-p-3 tw-text-left !tw-text-black tw-font-bold">Value</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr v-for="holding in area.portfolioHoldingsWithNoTaxStatus" :key="holding.ticker" class="tw-border-t">
                            <td class="tw-p-3">{{ holding.ticker }}</td>
                            <td class="tw-p-3">{{ holding.account || '' }}</td>
                            <td class="tw-p-3">${{ formatNumber(holding.dollars) }}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>

                  <!-- optimizedPortfolio Unclassified Holdings -->
                  <div v-if="area.optimizedPortfolioHoldingsWithNoTaxStatus?.length" class="tw-mb-4">
                    <div 
                      class="tw-flex tw-items-center tw-cursor-pointer tw-p-3 tw-bg-gray-100 tw-rounded" 
                      @click="toggleoptimizedPortfolioPanel"
                    >
                      <i class="fas fa-exclamation-circle tw-mr-2 tw-text-yellow-500"></i>
                      <span>Holdings with Unclassified Tax Status in {{ optimizedPortfolioName }}</span>
                      <i 
                        :class="[
                          'fas',
                          isoptimizedPortfolioPanelOpen ? 'fa-chevron-up' : 'fa-chevron-down',
                          'tw-ml-auto'
                        ]"
                      ></i>
                    </div>
                    <div v-show="isoptimizedPortfolioPanelOpen" class="tw-border tw-border-t-0 tw-p-4">
                      <table class="tw-w-full tw-border-collapse">
                        <thead>
                          <tr class="tw-bg-gray-50">
                            <th class="tw-p-3 tw-text-left !tw-text-black tw-font-bold">Symbol</th>
                            <th class="tw-p-3 tw-text-left !tw-text-black tw-font-bold">Account</th>
                            <th class="tw-p-3 tw-text-left !tw-text-black tw-font-bold">Value</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr v-for="holding in area.optimizedPortfolioHoldingsWithNoTaxStatus" :key="holding.ticker" class="tw-border-t">
                            <td class="tw-p-3">{{ holding.ticker }}</td>
                            <td class="tw-p-3">{{ holding.account || '' }}</td>
                            <td class="tw-p-3">${{ formatNumber(holding.dollars) }}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
                <EditablePrinciplesTable />
                <h3 class="tw-text-xl tw-font-bold tw-font-georgia">
                  Methodology
                </h3>
                <CheckupMethodologyWrapper
                  :component-name="area.methodology"
                  display-type="short"
                  class="tw-text-xs tw-font-poppins"
                />
                <CheckupMethodologyWrapper
                  :component-name="area.methodology"
                  display-type="long"
                  class="tw-text-xs tw-font-poppins tw-hidden"
                />
              </div>
            </div>
          </div>
          
          <AreaFooter />
          <CheckupDisclaimer class="tw-mt-16">
            <CheckupMethodologyWrapper
              :component-name="area.methodology"
              display-type="long"
            />
          </CheckupDisclaimer>
        </div>
        <AreaSidebar
          :area="area"
        >
          <IncludeInPresentationToggle :name="area?.name" />
          <template #other>
            <SpeakersNotes :notes="area?.explanation" />
            <!-- <AIChat /> -->
          </template>
        </AreaSidebar>
      </div>
  </AreaPageContainer>
</template>

<script>
import { Chart, PieController, ArcElement, Tooltip, Legend } from 'chart.js'
import AreaPageContainer from '@frontend/ui/features/checkup/components/analytics/shared/AreaPageContainer.vue';
import AssetLocationMethodology from '@frontend/ui/features/checkup/components/methodologies/AssetLocationMethodology.vue';
import AdvisorCoreStripes from '@frontend/ui/features/checkup/components/AdvisorCoreStripes.vue';
import AreaHeader from '@frontend/ui/features/checkup/components/analytics/shared/AreaHeader.vue';
import HighChartsWrapper from '@frontend/ui/components/charts/HighChartsWrapper.vue';
import AreaFooter from '@frontend/ui/features/checkup/components/analytics/shared/AreaFooter.vue';
import AreaSidebar from '@frontend/ui/features/checkup/components/sidebar/AreaSidebar.vue';
import IncludeInPresentationToggle from '@frontend/ui/features/checkup/components/sidebar/IncludeInPresentationToggle.vue';
import SpeakersNotes from '@frontend/ui/features/checkup/components/sidebar/SpeakersNotes.vue';
import AIChat from '@frontend/ui/features/checkup/components/sidebar/AIChat.vue';
import EditablePrinciplesTable from '@frontend/ui/features/checkup/components/sidebar/EditablePrinciplesTable.vue';
import ChartWrapper from '@frontend/ui/components/charts/ChartWrapper.vue';
import ChartClickthroughs from '@frontend/ui/features/checkup/components/ChartClickthroughs.vue';
import CheckupDisclaimer from '@frontend/ui/features/checkup/components/CheckupDisclaimer.vue';
import CheckupMethodologyWrapper from '@frontend/ui/features/checkup/components/methodologies/CheckupMethodologyWrapper.vue';
import { mapStores } from 'pinia';
import { useCheckupStore } from '@frontend/ui/stores/checkupStore';
import { useAdvisorStore } from '@frontend/ui/stores/advisorStore';
import { useChatStore } from '@frontend/ui/stores/chatStore';

Chart.register(PieController, ArcElement, Tooltip, Legend)

export default {
  name: 'AssetLocationArea',
  components: {
    AreaPageContainer,
    ChartWrapper,
    ChartClickthroughs,
    CheckupDisclaimer,
    AssetLocationMethodology,
    HighChartsWrapper,
    CheckupMethodologyWrapper,
    AdvisorCoreStripes,
    AreaHeader,
    AreaFooter,
    AreaSidebar,
    IncludeInPresentationToggle,
    SpeakersNotes,
    AIChat,
    EditablePrinciplesTable,
  },
  data() {
    return {
      chartClickData: null,
      isPortfolioPanelOpen: false,
      isoptimizedPortfolioPanelOpen: false,
    };
  },
  computed: {
    ...mapStores(useChatStore, useCheckupStore, useAdvisorStore),

    area() {
      return this.checkupStore.areasByName?.['Asset Location'];
    },
    chartSections() {
      const chartsections = new Set();
      this.area.chartData[0].series.forEach(series => {
        series.data.forEach(point => {
          chartsections.add(point.name);
        });
      });
      return Array.from(chartsections);
    },
    portfolioName() {
      const name = this.checkupStore.portfolio?.name;
      return name || 'Portfolio';
    },
    optimizedPortfolioName() {
      const name = this.checkupStore.optimizedPortfolio?.name;
      return name || 'Proposed';
    },
    chartText() {
      const fontSize = 14;
      const width = 300;
      const y = '120%';

      const css = {
        color: 'black',
        fontSize: `${fontSize}px`,
        fontWeight: 'bold',
        width: `${width}px`,
      };

      function getOffset(text) {
        const naturalWidth = text.length * fontSize * 0.5;
        return Math.min(naturalWidth, width) / 2;
      }

      return [
        {
          config: [
            this.portfolioName,
            '25%',
            y,
          ],
          css: {
            ...css,
            transform: `translateX(-${getOffset(this.portfolioName)}px)`,
          },
        },
        {
          config: [
            this.optimizedPortfolioName,
            '75%',
            y,
          ],
          css: {
            ...css,
            transform: `translateX(-${getOffset(this.optimizedPortfolioName)}px)`,
          },
        },
      ];
    },
    hasAnyCharts() {
      return (this.area?.portfolioChartData?.length > 0) || 
             (this.area?.optimizedPortfolioChartData?.length > 0);
    },
    hasPortfolio() {
      return this.area?.portfolioChartData?.[0]?.series?.length > 0;
    },
    hasOptimizedPortfolio() {
      return this.area?.optimizedPortfolioChartData?.[0]?.series?.length > 0;
    },
    taxStatuses() {
      const statuses = new Set();
      const addFromChartData = (chartData) => {
        if (chartData?.[0]?.series) {
          chartData[0].series.forEach(series => {
            statuses.add(series.name);
          });
        }
      };
      addFromChartData(this.area?.portfolioChartData);
      addFromChartData(this.area?.optimizedPortfolioChartData);
      return Array.from(statuses);
    },
    allAssetClasses() {
      const classes = new Set();
      const addFromChartData = (chartData) => {
        if (chartData?.[0]?.series) {
          chartData[0].series.forEach(series => {
            series.data.forEach(point => {
              classes.add(point.name);
            });
          });
        }
      };
      addFromChartData(this.area?.portfolioChartData);
      addFromChartData(this.area?.optimizedPortfolioChartData);
      return Array.from(classes);
    },
    portfolioTaxStatusValues() {
      const values = {};
      const items = this.checkupStore.portfolio?.uploaded_df_agg || [];
      items.forEach(item => {
        const taxStatus = item.account_tax_status || 'Unclassified';
        values[taxStatus] = (values[taxStatus] || 0) + (parseFloat(item.dollars) || 0);
      });
      return values;
    },
    portfolioTaxStatusAccountCounts() {
      const accountsByStatus = {};
      const items = this.checkupStore.portfolio?.uploaded_df_agg || [];
      items.forEach(item => {
        const taxStatus = item.account_tax_status || 'Unclassified';
        const account = item.account || 'Unknown';
        if (!accountsByStatus[taxStatus]) {
          accountsByStatus[taxStatus] = new Set();
        }
        accountsByStatus[taxStatus].add(account);
      });
      const counts = {};
      for (const [status, accounts] of Object.entries(accountsByStatus)) {
        counts[status] = accounts.size;
      }
      return counts;
    },
    optimizedPortfolioTaxStatusValues() {
      const values = {};
      const items = this.checkupStore.optimizedPortfolio?.uploaded_df_agg || [];
      items.forEach(item => {
        const taxStatus = item.account_tax_status || 'Unclassified';
        values[taxStatus] = (values[taxStatus] || 0) + (parseFloat(item.dollars) || 0);
      });
      return values;
    },
    optimizedPortfolioTaxStatusAccountCounts() {
      const accountsByStatus = {};
      const items = this.checkupStore.optimizedPortfolio?.uploaded_df_agg || [];
      items.forEach(item => {
        const taxStatus = item.account_tax_status || 'Unclassified';
        const account = item.account || 'Unknown';
        if (!accountsByStatus[taxStatus]) {
          accountsByStatus[taxStatus] = new Set();
        }
        accountsByStatus[taxStatus].add(account);
      });
      const counts = {};
      for (const [status, accounts] of Object.entries(accountsByStatus)) {
        counts[status] = accounts.size;
      }
      return counts;
    },
    accountTypes() {
      const types = new Set();
      
      // Add types from portfolio data
      if (this.area?.portfolioChartData) {
        this.area.portfolioChartData.forEach(chart => {
          if (chart.series) {
            chart.series.forEach(series => {
              series.data.forEach(point => {
                types.add(point.name);
              });
            });
          }
        });
      }
      
      // Add types from optimizedPortfolio data
      if (this.area?.optimizedPortfolioChartData) {
        this.area.optimizedPortfolioChartData.forEach(chart => {
          if (chart.series) {
            chart.series.forEach(series => {
              series.data.forEach(point => {
                types.add(point.name);
              });
            });
          }
        });
      }
      
      return Array.from(types);
    }
  },
  methods: {
    handleChartClick(data) {
      this.chartClickData = data;
    },
    handlePortfolioSeriesPointClick(pointData) {
      pointData = {
        ...pointData,
        value: {
          label: this.checkupStore.portfolio?.name, // Set label to portfolio name
          taxability: pointData.value.label // Keep original label as taxability
        }
      };
      this.chartClickData = pointData;
    },
    handleoptimizedPortfolioSeriesPointClick(pointData) {
      pointData = {
        ...pointData,
        value: {
          label: this.checkupStore.optimizedPortfolio?.name, // Set label to optimizedPortfolio name
          taxability: pointData.value.label // Keep original label as taxability
        }
      };
      this.chartClickData = pointData;
    },
    formatNumber(value) {
      return new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(value);
    },
    formatCurrency(value) {
      return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(value || 0);
    },
    getPercentageForAssetClass(series, assetClass) {
      if (!series) return '0%';
      const dataPoint = series.data.find(point => point.name === assetClass);
      let normalized = dataPoint ? (dataPoint.y * 100).toFixed(0) + '%' : '0%';
      if (normalized === '-0%') {
        normalized = '0%';
      }
      return normalized;
    },
    getSeriesByName(chartData, name) {
      if (!chartData?.[0]?.series) return null;
      return chartData[0].series.find(s => s.name === name) || null;
    },
    togglePortfolioPanel() {
      this.isPortfolioPanelOpen = !this.isPortfolioPanelOpen;
    },
    toggleoptimizedPortfolioPanel() {
      this.isoptimizedPortfolioPanelOpen = !this.isoptimizedPortfolioPanelOpen;
    },
    getLegendColor(type) {
      // Use the same colors defined in checkupFunctions.js
      const colors = {
        'US Stocks': '#4A90E2',
        'International Stocks': '#FF6B6B',
        'Taxable Bonds': '#FF8C00',
        'Municipal Bonds': '#008080',
        'Cash': '#4CAF50',
        'Alternatives': '#9B51E0',
        'Unclassified': '#BDBDBD'
      };
      return colors[type] || '#BDBDBD';  // Default color if type not found
    }
  }
};
</script>

<style scoped>
ul.tw-list-disc {
  list-style-type: disc;
}
.v-table {
  width: 100%;
}

.v-table th {
  font-weight: bold;
  text-align: left;
  padding: 12px;
  background-color: #f5f5f5;
}

.v-table td {
  padding: 12px;
  border-bottom: 1px solid #eeeeee;
}
</style>
